import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Card, MetricCard, Plate } from '../components/Card';
import { getMarketingFunnel } from '../api';

const SHORT = {
  'Я.директ kluch-podkluch.ru / bobermaster.com': 'Я.директ',
  'Звонок на физический номер (звонки с карт)': 'Звонок',
  'Билайн АТС 9052120610 | Прямой номер': 'Билайн',
  'Яндекс органика': 'Яндекс',
  'Гугл органика': 'Гугл',
};

const tooltipStyle = {
  background: 'var(--bg3)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 12,
};

const pill = (active) => ({
  height: 34,
  padding: '0 14px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
  background: active ? 'var(--accent)' : 'var(--bg2)',
  color: active ? '#fff' : 'var(--text2)',
  whiteSpace: 'nowrap',
});

function num(n) {
  return Math.round(n || 0).toLocaleString('ru-RU');
}

function rub(n) {
  return `${num(n)} ₽`;
}

function shortMoney(n) {
  const v = Math.abs(Math.round(n || 0));
  const sign = n < 0 ? '−' : '';
  if (v >= 1000000) return `${sign}${(v / 1000000).toFixed(1).replace('.', ',')} млн`;
  if (v >= 1000) return `${sign}${Math.round(v / 1000)} тыс`;
  return sign + String(v);
}

function pct(part, whole) {
  if (!whole) return '—';
  return `${(100 * part / whole).toFixed(1).replace('.', ',')}%`;
}

function labelOf(title) {
  return (title || '').replace(' 2026', '');
}

function signed(n, money) {
  const body = money ? rub(Math.abs(n)) : num(Math.abs(n));
  if (n > 0) return `+${body}`;
  if (n < 0) return `−${body}`;
  return body;
}

function Trend({ cur, prev, label, money, invert }) {
  if (!label || prev == null) return null;
  const diff = (cur || 0) - (prev || 0);
  const rel = prev ? (100 * diff / Math.abs(prev)) : null;
  const up = diff > 0;
  const down = diff < 0;
  const better = invert ? down : up;
  const worse = invert ? up : down;
  const color = better ? '#0e9f6e' : worse ? '#e04848' : 'var(--text2)';
  const arrow = up ? '▲' : down ? '▼' : '●';
  const relText = rel == null
    ? 'с нуля'
    : `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${Math.abs(rel).toFixed(1).replace('.', ',')}%`;
  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)', whiteSpace: 'nowrap', lineHeight: 1.35 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color }}>{arrow} {signed(diff, money)}</div>
      <div style={{ fontSize: 12, marginTop: 2 }}>
        <span style={{ color, fontWeight: 700 }}>{relText}</span>
        <span style={{ color: 'var(--text2)' }}> {label}</span>
      </div>
    </div>
  );
}

function Point({ cx, cy, payload, selected, color }) {
  if (cx == null || cy == null) return null;
  const on = payload.label === selected;
  return (
    <circle cx={cx} cy={cy} r={on ? 6 : 4} fill={on ? color : 'var(--bg)'} stroke={color} strokeWidth={2} />
  );
}

export default function MarketingFunnel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [grain, setGrain] = useState('months');
  const [periodTitle, setPeriodTitle] = useState('');

  const load = (refresh = false) => {
    setLoading(true);
    setErr('');
    getMarketingFunnel(refresh)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || e.message || 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(false); }, []);

  const periods = data?.[grain] || [];

  useEffect(() => {
    if (!periods.length) return;
    if (periods.some((b) => b.title === periodTitle)) return;
    const open = [...periods].reverse().find((b) => b.open) || periods[periods.length - 1];
    setPeriodTitle(open.title);
  }, [periods, periodTitle]);

  const block = periods.find((b) => b.title === periodTitle) || periods[periods.length - 1];
  const selected = labelOf(block?.title);
  const cmp = block?.compare?.totals;
  const cmpLabel = block?.compare?.label;

  const trend = useMemo(() => periods.map((b) => ({
    label: labelOf(b.title),
    leads: b.totals.leads || 0,
    rev: b.totals.rev || 0,
  })), [periods]);

  const th = (i) => ({
    textAlign: i === 0 ? 'left' : 'right',
    padding: '8px 10px',
    color: 'var(--text2)',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  });

  const td = (i, lost) => ({
    padding: '7px 10px',
    textAlign: i === 0 ? 'left' : 'right',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--border)',
    color: lost ? '#e04848' : 'var(--text1)',
  });

  return (
    <div>
      <div className="kp-hero" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Plate>Розница · маркетинг</Plate>
          <h1 style={{ margin: 0 }}>Воронка розницы</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
            {data?.as_of ? `Данные на ${data.as_of} МСК. Лид — сделка, один канал.` : 'Лид — сделка, один канал.'}
          </div>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} style={pill(false)}>
          {loading ? 'Считаю…' : 'Обновить'}
        </button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button type="button" style={pill(grain === 'months')} onClick={() => setGrain('months')}>Месячный</button>
          <button type="button" style={pill(grain === 'weeks')} onClick={() => setGrain('weeks')}>Недельный</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 12 }}>
          {periods.map((b) => (
            <button key={b.title} type="button" style={pill(block?.title === b.title)} onClick={() => setPeriodTitle(b.title)}>
              {labelOf(b.title)}{b.open ? ' · идёт' : ''}
            </button>
          ))}
        </div>
      </Card>

      {err ? <div style={{ color: '#e04848', marginBottom: 16 }}>{err}</div> : null}
      {loading && !data ? <div style={{ color: 'var(--text2)' }}>Открываю отчёт…</div> : null}

      {data && block ? (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <MetricCard title="Лиды" value={num(block.totals.leads)} color="#00a7dc"
              footer={<Trend cur={block.totals.leads} prev={cmp?.leads} label={cmpLabel} />} />
            <MetricCard title="Выполнено" value={num(block.totals.won)} sub={pct(block.totals.won, block.totals.leads)} color="#0e9f6e"
              footer={<Trend cur={block.totals.won} prev={cmp?.won} label={cmpLabel} />} />
            <MetricCard title="Сумма заказов" value={rub(block.totals.rev)} color="#e08a00"
              footer={<Trend cur={block.totals.rev} prev={cmp?.rev} label={cmpLabel} money />} />
            <MetricCard title="Провал" value={num(block.totals.lose)} sub={rub(block.totals.lost_rev)} color="#e04848"
              footer={<Trend cur={block.totals.lose} prev={cmp?.lose} label={cmpLabel} invert />} />
            <MetricCard title="В работе" value={num(block.totals.process)} sub={rub(block.totals.pipe_rev)} color="#00a7dc"
              footer={<Trend cur={block.totals.process} prev={cmp?.process} label={cmpLabel} />} />
          </div>

          <Card title={`${grain === 'months' ? 'Месяц' : 'Неделя'} ${selected}${block.open ? ' · идёт' : ''}`} style={{ marginBottom: 16 }}>
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Канал', 'Зашло', 'Закрыто', 'Конверсия', 'Провал', 'Выручка', 'Упущено', 'В работе'].map((h, i) => (
                      <th key={h} style={th(i)}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ fontWeight: 800 }}>
                    {[
                      'Всего', num(block.totals.leads), num(block.totals.won), pct(block.totals.won, block.totals.leads),
                      num(block.totals.lose), rub(block.totals.rev), rub(block.totals.lost_rev), num(block.totals.process),
                    ].map((v, i) => (
                      <td key={i} style={td(i, i === 6)}>{v}</td>
                    ))}
                  </tr>
                  {block.rows.map((r) => (
                    <tr key={r.name}>
                      {[
                        SHORT[r.name] || r.name, num(r.leads), num(r.won), pct(r.won, r.leads),
                        num(r.lose), rub(r.rev), rub(r.lost_rev), num(r.process),
                      ].map((v, i) => (
                        <td key={i} style={td(i, i === 6)}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {block.totals.apology ? (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text2)' }}>
                Извинения: {num(block.totals.apology)}. Входят в лиды, в выполненные и провал не входят.
              </div>
            ) : null}
          </Card>

          <Card title={grain === 'months' ? 'Динамика по месяцам' : 'Динамика по неделям'} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Количество лидов</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: 'var(--text2)', fontSize: 12 }} interval={0} />
                <YAxis tick={{ fill: 'var(--text2)', fontSize: 11 }} width={48} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [num(v), 'Лиды']} />
                <Line
                  type="monotone"
                  dataKey="leads"
                  stroke="#00a7dc"
                  strokeWidth={2.5}
                  dot={(p) => <Point {...p} selected={selected} color="#00a7dc" />}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>

            <div style={{ fontSize: 13, fontWeight: 700, margin: '18px 0 8px' }}>Сумма выполненных заказов</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: 'var(--text2)', fontSize: 12 }} interval={0} />
                <YAxis tick={{ fill: 'var(--text2)', fontSize: 11 }} tickFormatter={shortMoney} width={56} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [rub(v), 'Выполнено']} />
                <Line
                  type="monotone"
                  dataKey="rev"
                  stroke="#e08a00"
                  strokeWidth={2.5}
                  dot={(p) => <Point {...p} selected={selected} color="#e08a00" />}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
